import React, { useEffect, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import {
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core/";
import { useTranslation } from "react-i18next";

import { arrayOfNumbers } from "../../../services/util.service";

import useStyles from "./RepLIChart.style";

const GoalLabel = (props) => {
  return (
    <svg
      width="30"
      height="30"
      x={props.viewBox.x + props.viewBox.width}
      y={props.viewBox.y - 15}
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <title>Layer 1</title>
        <path
          fill="#ffff00"
          stroke="#ffffaa"
          d="m2.5,10.90327l8.40332,0l2.59669,-8.40327l2.59669,8.40327l8.40331,0l-6.79841,5.19345l2.59682,8.40327l-6.79842,-5.19359l-6.79841,5.19359l2.59682,-8.40327l-6.79841,-5.19345z"
          id="svg_1"
          strokeWidth="2"
        />
      </g>
    </svg>
  );
};

const LiveBirthShape = (props) => {
  const { cx, cy } = props;

  if (props.intention === "Unintended")
    return (
      <svg x={cx - 10} y={cy - 10} height="20" width="20">
        <polygon points="0,20 10,0 20,20" style={{ fill: "lime" }}>
          <title>Unintended Live Birth - {props.count}</title>
        </polygon>
      </svg>
    );

  return (
    <svg x={cx} y={cy} height="20" width="20" overflow="visible">
      <circle r="10" style={{ fill: "lime" }}>
        <title>Intended Live Birth - {props.count}</title>
      </circle>
    </svg>
  );
};

const InterruptedBirthShape = (props) => {
  const { cx, cy } = props;

  if (
    props.birthData.outcome === "Miscarriage" &&
    props.birthData.isProviderView == "true"
  )
    return (
      <svg x={cx - 10} y={cy - 10} height="20px" width="20px">
        <polygon points="0,10 10,0 20,10 10,20" style={{ fill: "purple" }}>
          <title>Miscarrage - {props.birthData.count}</title>
        </polygon>
      </svg>
    );
  if (props.birthData.outcome === "Ectopic or Tubal")
    return (
      <svg x={cx - 10} y={cy - 10} height="20px" width="20px">
        <polygon points="0,10 10,0 20,10 10,20" style={{ fill: "olive" }}>
          <title>Ectopic or Tubal - {props.birthData.count}</title>
        </polygon>
      </svg>
    );
  if (
    props.birthData.outcome === "Abortion" &&
    props.birthData.isProviderView == "true"
  )
    return (
      <svg x={cx - 10} y={cy - 10} height="20px" width="20px">
        <rect width="20px" height="20px" style={{ fill: "blue" }}>
          <title>Abortion - {props.birthData.count}</title>
        </rect>
      </svg>
    );

  return null;
};

const StillbirthShape = (props) => {
  const { cx, cy } = props;

  return (
    <svg x={cx - 10} y={cy - 10} height="20px" width="20px">
      <polygon points="0,10 10,0 20,10 10,20" style={{ fill: "deepskyblue" }}>
        <title>Still Birth - {props.count}</title>
      </polygon>
    </svg>
  );
};

const PlacedAdoptionShape = (props) => {
  const { cx, cy } = props;

  return (
    <svg x={cx - 10} y={cy - 10} height="20px" width="20px">
      <polygon points="0,10 10,0 20,10 10,20" style={{ fill: "deeppink" }}>
        <title>Placed Adoption - {props.count}</title>
      </polygon>
    </svg>
  );
};

const AdoptionShape = (props) => {
  const { cx, cy } = props;

  return (
    <svg x={cx} y={cy} height="20" width="20" overflow="visible">
      <circle r="10" style={{ fill: "darkorange" }}>
        <title>Adoption - {props.count}</title>
      </circle>
    </svg>
  );
};

const StepchildrenShape = (props) => {
  const { cx, cy } = props;

  return (
    <svg x={cx} y={cy} height="20" width="20" overflow="visible">
      <circle r="10" style={{ fill: "yellow" }}>
        <title>Stepchildren - {props.count}</title>
      </circle>
    </svg>
  );
};

const ChildrenDeathShape = (props) => {
  const { cx, cy } = props;

  return (
    <svg x={cx - 10} y={cy - 10} height="20px" width="20px">
      <rect width="20px" height="20px" style={{ fill: "red" }}>
        <title>Children Loss - {props.count}</title>{" "}
      </rect>
    </svg>
  );
};

const RepLIChart = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [yearFrame, setYearFrame] = useState([]);
  const [childbearingYearFrame, setChildbearingYearFrame] = useState([]);
  const [chartData, setChartData] = useState();
  const [liveBirths, setLiveBirths] = useState([]);
  const [stillbirth, setStillbirth] = useState([]);
  const [placedAdoption, setPlacedAdoption] = useState([]);
  const [adoptions, setAdoptions] = useState([]);
  const [stepchildren, setStepchildren] = useState([]);
  const [childrenLoss, setChildrenLoss] = useState([]);
  const [interruptedBirths, setInterruptedBirths] = useState([]);

  const cancerDiagnosisYears = []; //props.profile.cancerDiagnosis.map(diagnosis => diagnosis.year);
  const birthYear = new Date(props.profile.dateOfBirth).getFullYear();
  const firstPeriodYear = birthYear + parseInt(props.profile.firstPeriodAge);
  const sexuallyActiveYear =
    birthYear + parseInt(props.profile.sexuallyActiveAge);
  const menopauseYear = birthYear + parseInt(props.profile.menopauseAge);
  const printableFertilityPreservation =
    props.profile.fertilityPreservation &&
    props.profile.fertilityPreservation.filter(
      (method) => method.method !== "None"
    );
  const printableFertilityTreatment =
    props.profile.fertilityTreatment &&
    props.profile.fertilityTreatment.filter(
      (method) => method.treatment !== "None"
    );

  useEffect(() => {
    const { profile } = props;

    let yearOfBirth = new Date(profile.dateOfBirth).getFullYear();
    setYearFrame(arrayOfNumbers(yearOfBirth + 10, 51));

    if (profile.childbearingYearFrame)
      setChildbearingYearFrame(
        profile.childbearingYearFrame.map((year) => year + yearOfBirth)
      );

    // StillBirth Pregnancies

    let stillbirthPregnancies =
      profile.pregnancies &&
      profile.pregnancies.filter((pregnancy) => {
        return (
          pregnancy.outcome === "Live Birth" && pregnancy.stillbirthQty > 0
        );
      });
    let tempStillBirth = [];
    stillbirthPregnancies &&
      stillbirthPregnancies.map((pregnancy) => {
        let stillBirthIndex = tempStillBirth.findIndex(
          (stillBirth) => stillBirth.x == pregnancy.year
        );

        if (stillBirthIndex != -1) {
          tempStillBirth[stillBirthIndex].count =
            tempStillBirth[stillBirthIndex].count + pregnancy.stillbirthQty;
          tempStillBirth[stillBirthIndex].y =
            tempStillBirth[stillBirthIndex].y + pregnancy.stillbirthQty;
        } else {
          tempStillBirth.push({
            x: pregnancy.year,
            y: pregnancy.stillbirthQty,
            count: pregnancy.stillbirthQty,
          });
        }
      });
    setStillbirth(tempStillBirth);

    // Placed Adoption Pregnancies

    let placedAdoptionPregnancies =
      profile.pregnancies &&
      profile.pregnancies.filter((pregnancy) => {
        return (
          pregnancy.outcome === "Live Birth" && pregnancy.placedAdoptionQty > 0
        );
      });
    let tempPlacedAdoptions = [];
    placedAdoptionPregnancies &&
      placedAdoptionPregnancies.map((pregnancy) => {
        let placedAdoptionIndex = tempPlacedAdoptions.findIndex(
          (placedAdoption) => placedAdoption.x == pregnancy.year
        );

        if (placedAdoptionIndex != -1) {
          tempPlacedAdoptions[placedAdoptionIndex].count =
            tempPlacedAdoptions[placedAdoptionIndex].count +
            pregnancy.placedAdoptionQty;
          tempPlacedAdoptions[placedAdoptionIndex].y =
            tempPlacedAdoptions[placedAdoptionIndex].y +
            pregnancy.placedAdoptionQty;
        } else {
          tempPlacedAdoptions.push({
            x: pregnancy.year,
            y: pregnancy.placedAdoptionQty,
            count: pregnancy.placedAdoptionQty,
          });
        }
      });
    setPlacedAdoption(tempPlacedAdoptions);

    // Interrupted Pregnancies

    let interruptedPregnancies =
      profile.pregnancies &&
      profile.pregnancies.filter((pregnancy) => {
        return pregnancy.outcome !== "Live Birth";
      });
    let tempInterruptedBirths = [];
    interruptedPregnancies &&
      interruptedPregnancies.map((pregnancy) => {
        let interruptedBirthIndex = tempInterruptedBirths.findIndex(
          (interruptedBirth) =>
            interruptedBirth.x == pregnancy.year &&
            interruptedBirth.outcome == pregnancy.outcome
        );

        if (interruptedBirthIndex != -1) {
          if (pregnancy.isProviderView == "true") {
            tempInterruptedBirths[interruptedBirthIndex].count =
              tempInterruptedBirths[interruptedBirthIndex].count + 1;
          }
        } else {
          tempInterruptedBirths.push({
            x: pregnancy.year,
            y: 1,
            outcome: pregnancy.outcome,
            isProviderView: pregnancy.isProviderView,
            count: 1,
          });
        }
      });
    setInterruptedBirths(tempInterruptedBirths);
  }, [props]);

  useEffect(() => {
    const { profile } = props;

    setChartData(
      yearFrame.map((year) => {
        let pregnanciesBeforeYear =
          profile.pregnancies &&
          profile.pregnancies.filter((pregnancy) => {
            return pregnancy.year <= year;
          });
        let totalLiveBirth =
          pregnanciesBeforeYear &&
          pregnanciesBeforeYear.reduce((a, b) => a + (b.liveBirthQty || 0), 0);
        // let stillBirth = pregnanciesBeforeYear.reduce((a, b) => a + (b.stillbirthQty || 0), 0);
        let totalPlacedAdotion =
          pregnanciesBeforeYear &&
          pregnanciesBeforeYear.reduce(
            (a, b) => a + (b.placedAdoptionQty || 0),
            0
          );

        let adoptionsBeforeYear =
          profile.adoptions &&
          profile.adoptions.filter((adoption) => {
            return adoption.year <= year;
          });
        let totalAdoption =
          adoptionsBeforeYear &&
          adoptionsBeforeYear.reduce((a, b) => a + (b.childrenQty || 0), 0);

        let stepchildrenBeforeYear =
          profile.stepchildren &&
          profile.stepchildren.filter((stepchildren) => {
            return stepchildren.year <= year;
          });
        let totalStepchildren =
          stepchildrenBeforeYear &&
          stepchildrenBeforeYear.reduce(
            (a, b) => a + (b.stepchildrenQty || 0),
            0
          );

        let childrenLossBeforeYear =
          profile.childrenLoss &&
          profile.childrenLoss.filter((childrenLoss) => {
            return childrenLoss.year <= year;
          });
        let totalChildrenLoss =
          childrenLossBeforeYear &&
          childrenLossBeforeYear.reduce((a, b) => a + (b.childrenQty || 0), 0);
        let relativeAccumulatedOutcomeQty =
          totalLiveBirth -
          totalPlacedAdotion +
          totalAdoption +
          totalStepchildren -
          totalChildrenLoss;

        // if (year > currentYear) {
        // 	relativeAccumulatedOutcomeQty = null;
        // }

        return { year: year, fpq: relativeAccumulatedOutcomeQty };
      })
    );
  }, [props, yearFrame]);

  useEffect(() => {
    const { profile } = props;

    if (!chartData) return;

    // live birth pregencies

    let liveBirthPregnancies =
      profile.pregnancies &&
      profile.pregnancies.filter((pregnancy) => {
        return pregnancy.outcome === "Live Birth" && pregnancy.liveBirthQty > 0;
      });

    let tempLiveBirths = [];
    liveBirthPregnancies &&
      liveBirthPregnancies.flatMap((pregnancy) => {
        let fqpLineYValueForYear = chartData
          .filter((data) => data.year === pregnancy.year)
          .reduce((a, b) => a + (b.fpq || 0), 0);

        let liveBirthIndex = tempLiveBirths.findIndex(
          (liveBirth) => liveBirth.x == pregnancy.year
        );

        if (liveBirthIndex != -1) {
          tempLiveBirths[liveBirthIndex].count =
            tempLiveBirths[liveBirthIndex].count + pregnancy.liveBirthQty;
        } else {
          tempLiveBirths.push({
            x: pregnancy.year,
            y: fqpLineYValueForYear,
            intention: pregnancy.intention,
            count: pregnancy.liveBirthQty,
          });
        }
      });

    setLiveBirths(tempLiveBirths);

    // Adoption

    let tempAdoption = [];

    profile.adoptions &&
      profile.adoptions.flatMap((adoption) => {
        //let fqpLineYValueForYear = adoption.childrenQty;
        let fqpLineYValueForYear = chartData
          .filter((data) => data.year === adoption.year)
          .reduce((a, b) => a + (b.fpq || 0), 0);

        let adoptionIndex = tempAdoption.findIndex(
          (adp) => adp.x == adoption.year
        );

        if (adoptionIndex != -1) {
          tempAdoption[adoptionIndex].count =
            tempAdoption[adoptionIndex].count + adoption.childrenQty;
        } else {
          tempAdoption.push({
            x: adoption.year,
            y: fqpLineYValueForYear,
            count: adoption.childrenQty,
          });
        }
      });

    setAdoptions(tempAdoption);

    let tempStepChildren = [];

    profile.stepchildren &&
      profile.stepchildren.flatMap((stepchildren) => {
        // let fqpLineYValueForYear = stepchildren.stepchildrenQty;
        let fqpLineYValueForYear = chartData
          .filter((data) => data.year === stepchildren.year)
          .reduce((a, b) => a + (b.fpq || 0), 0);

        let stepChildrenIndex = tempStepChildren.findIndex(
          (sc) => sc.x == stepchildren.year
        );

        if (stepChildrenIndex != -1) {
          tempStepChildren[stepChildrenIndex].count =
            tempStepChildren[stepChildrenIndex].count +
            stepchildren.stepchildrenQty;
        } else {
          tempStepChildren.push({
            x: stepchildren.year,
            y: fqpLineYValueForYear,
            count: stepchildren.stepchildrenQty,
          });
        }
      });

    setStepchildren(tempStepChildren);

    let tempChildrenLoss = [];

    profile.childrenLoss &&
      profile.childrenLoss.flatMap((loss) => {
        // let fqpLineYValueForYear = loss.childrenQty;
        let fqpLineYValueForYear = chartData
          .filter((data) => data.year === loss.year)
          .reduce((a, b) => a + (b.fpq || 0), 0);

        let childrenLossIndex = tempChildrenLoss.findIndex(
          (sc) => sc.x == loss.year
        );

        if (childrenLossIndex != -1) {
          tempChildrenLoss[childrenLossIndex].count =
            tempChildrenLoss[childrenLossIndex].count + loss.childrenQty;
        } else {
          tempChildrenLoss.push({
            x: loss.year,
            y: fqpLineYValueForYear,
            count: loss.childrenQty,
          });
        }
      });

    setChildrenLoss(tempChildrenLoss);
  }, [chartData, props]);

  return (
    <>
      <Box style={{ display: "flex", flexDirection: "row" }}>
        <Box className={classes.legend}>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10" width="20">
              <line
                x1="0"
                y1="5"
                x2="20"
                y2="5"
                style={{ stroke: "yellow", strokeWidth: 3 }}
              />
            </svg>
            &nbsp;{t("repliQuestionnaire.label.fpqGoal")}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10" width="20">
              <line
                x1="0"
                y1="5"
                x2="20"
                y2="5"
                style={{ stroke: "blue", strokeWidth: 3 }}
              />
            </svg>
            &nbsp;FPQ
          </Box>
          <Box className={classes.legendItem}>
          <svg className={classes.svgIcon} overflow="visible" width="20" height="20">
            <polygon
              points="10,1 12,7 18,7 13,11 15,17 10,13 5,17 7,11 2,7 8,7"
              fill="yellow"
            />
          </svg>
          &nbsp;{t("repliQuestionnaire.label.desiredChildren")}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10" width="10" overflow="visible">
              <circle cx={5} cy={5} r="5" style={{ fill: "lime" }} />
            </svg>
            &nbsp;&nbsp;
            {`${t("repliQuestionnaire.label.intention.intended")} ${t(
              "repliQuestionnaire.label.outcome.liveBirth"
            )}`}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10" width="10" overflow="visible">
              <polygon points="0,10 5,0 10,10" style={{ fill: "lime" }} />
            </svg>
            &nbsp;&nbsp;
            {`${t("repliQuestionnaire.label.intention.unintended")} ${t(
              "repliQuestionnaire.label.outcome.liveBirth"
            )}`}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10" width="10" overflow="visible">
              <rect
                width="10"
                height="10"
                style={{
                  fill: "blue",
                  fillOpacity: 0.3,
                  stroke: "blue",
                  strokeOpacity: 1,
                }}
              />
            </svg>
            &nbsp;{t("module.provider.repli.chart.childbearing")}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10" width="10" overflow="visible">
              <polygon points="0,5 5,0 10,5 5,10" style={{ fill: "purple" }} />
            </svg>
            &nbsp;{t("repliQuestionnaire.label.outcome.miscarriage")}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10" width="10" overflow="visible">
              <polygon points="0,5 5,0 10,5 5,10" style={{ fill: "olive" }} />
            </svg>
            &nbsp;{t("repliQuestionnaire.label.outcome.ectopicTubal")}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10px" width="10px">
              <rect width="10px" height="10px" style={{ fill: "blue" }} />
            </svg>
            &nbsp;{t("repliQuestionnaire.label.outcome.abortion")}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10px" width="10px" overflow="visible">
              <polygon
                points="0,5 5,0 10,5 5,10"
                style={{ fill: "deepskyblue" }}
              />
            </svg>
            &nbsp;{t("repliQuestionnaire.label.stillbirth")}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10px" width="10px" overflow="visible">
              <polygon
                points="0,5 5,0 10,5 5,10"
                style={{ fill: "deeppink" }}
              />
            </svg>
            &nbsp;{t("repliQuestionnaire.label.placedAdoption")}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10" width="10" overflow="visible">
              <circle cx={5} cy={5} r="5" style={{ fill: "darkorange" }} />
            </svg>
            &nbsp;&nbsp;{t("common.substantive.adoption")}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10" width="10" overflow="visible">
              <circle cx={5} cy={5} r="5" style={{ fill: "yellow" }} />
            </svg>
            &nbsp;&nbsp;{t("common.substantive.stepchildren")}
          </Box>
          <Box className={classes.legendItem}>
            <svg className={classes.svgIcon} height="10px" width="10px">
              <rect width="20px" height="20px" style={{ fill: "red" }} />
            </svg>
            &nbsp;&nbsp;{t("repliQuestionnaire.label.childrenLoss")}
          </Box>
        </Box>
        <Box className={classes.chartContainer}>
          <ResponsiveContainer height={400} className={classes.chart}>
            <LineChart data={chartData} margin={{ right: 30, bottom: 5 }}>
              <CartesianGrid />
              <XAxis
                dataKey="year"
                interval={0}
                angle={-90}
                tickMargin={15}
                tick={{ dx: -5 }}
              />
              <YAxis
                dataKey="fpq"
                allowDecimals={false}
                domain={[0, "dataMax + 4"]}
                tickCount={10}
              />
              <ReferenceArea
                y1={props.profile.desiredChildrenQty}
                fill="#612F6F"
                fillOpacity={0.3}
              />
              <ReferenceArea
                y1={0}
                y2={props.profile.desiredChildrenQty}
                fill="#A0F8BD"
                fillOpacity={0.3}
              />
              <ReferenceLine
                y={props.profile.desiredChildrenQty}
                stroke="yellow"
                strokeWidth={3}
                label={<GoalLabel />}
              />
              <ReferenceArea
                x1={childbearingYearFrame[0]}
                x2={childbearingYearFrame[1]}
                fill="blue"
                fillOpacity={0.3}
                stroke="blue"
                strokeOpacity={1}
              />
              <Line dataKey="fpq" dot={false} strokeWidth={3} />
              {liveBirths.map((birth, i) => (
                <ReferenceDot
                  key={`liveBirth${i}`}
                  x={birth.x}
                  y={birth.y}
                  shape={
                    <LiveBirthShape
                      intention={birth.intention}
                      count={birth.count}
                    />
                  }
                />
              ))}
              {interruptedBirths.map((birth, i) => (
                <ReferenceDot
                  key={`interruptedBirth${i}`}
                  x={birth.x}
                  y={birth.y}
                  shape={<InterruptedBirthShape birthData={birth} />}
                />
              ))}
              {stillbirth.map((birth, i) => (
                <ReferenceDot
                  key={`stillbirth${i}`}
                  x={birth.x}
                  y={birth.y}
                  shape={<StillbirthShape count={birth.count} />}
                />
              ))}
              {adoptions.map((adoption, i) => (
                <ReferenceDot
                  key={`adoption${i}`}
                  x={adoption.x}
                  y={adoption.y}
                  shape={<AdoptionShape count={adoption.count} />}
                />
              ))}
              {stepchildren.map((stepchildren, i) => (
                <ReferenceDot
                  key={`stepchildren${i}`}
                  x={stepchildren.x}
                  y={stepchildren.y}
                  shape={<StepchildrenShape count={stepchildren.count} />}
                />
              ))}
              {placedAdoption.map((birth, i) => (
                <ReferenceDot
                  key={`placedAdoption${i}`}
                  x={birth.x}
                  y={birth.y}
                  shape={<PlacedAdoptionShape count={birth.count} />}
                />
              ))}
              {childrenLoss.map((loss, i) => (
                <ReferenceDot
                  key={`childrenLoss${i}`}
                  x={loss.x}
                  y={loss.y}
                  shape={<ChildrenDeathShape count={loss.count} />}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>
      <TableContainer
        style={{
          overflow: "auto",
          height: "auto",
          padding: "0 10px 10px 10px",
        }}
        component={Box}
        className={classes.table}
      >
        <Table aria-label="repLI table" size="small">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell colSpan={yearFrame.length} align="center">
                Year
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell></TableCell>
              {yearFrame.map((year) => (
                <TableCell key={year}>'{year.toString().slice(-2)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableGeneralBody}>
            {menopauseYear ? (
              <TableRow hover>
                <TableCell>
                  {t("module.provider.repli.chart.menstruation")}
                </TableCell>
                {yearFrame.map((year) => {
                  let style =
                    menopauseYear === year ? { backgroundColor: "black" } : {};
                  return <TableCell key={year} style={style}></TableCell>;
                })}
              </TableRow>
            ) : (
              <TableRow hover>
                <TableCell>
                  {t("module.provider.repli.chart.menstruation")}
                </TableCell>
                {yearFrame.map((year) => {
                  return <TableCell key={year}></TableCell>;
                })}
              </TableRow>
            )}
            {sexuallyActiveYear ? (
              <TableRow hover>
                <TableCell>
                  {t("module.provider.repli.chart.sexualDebut")}
                </TableCell>
                {yearFrame.map((year) => {
                  let style =
                    sexuallyActiveYear === year
                      ? { backgroundColor: "black" }
                      : {};
                  return <TableCell key={year} style={style}></TableCell>;
                })}
              </TableRow>
            ) : (
              <TableRow hover>
                <TableCell>
                  {t("module.provider.repli.chart.sexualDebut")}
                </TableCell>
                {yearFrame.map((year) => {
                  return <TableCell key={year}></TableCell>;
                })}
              </TableRow>
            )}
            {childbearingYearFrame && (
              <TableRow hover>
                <TableCell>
                  {t("module.provider.repli.chart.childbearing")}
                </TableCell>
                {yearFrame.map((year) => {
                  let style =
                    year >= childbearingYearFrame[0] &&
                    year <= childbearingYearFrame[1]
                      ? { backgroundColor: "black" }
                      : {};
                  return <TableCell key={year} style={style}></TableCell>;
                })}
              </TableRow>
            )}
          </TableBody>
          {props.profile.contraception &&
            props.profile.contraception.length > 0 && (
              <TableBody className={classes.tableContraceptivesBody}>
                <TableRow>
                  <TableCell colSpan={yearFrame.length + 1}>
                    {t("common.substantive.contraception")}
                  </TableCell>
                </TableRow>
                {props.profile.contraception.map((method, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{method.method}</TableCell>
                    {yearFrame.map((year) => {
                      let yearTo =
                        method.yearTo === "Current method"
                          ? new Date().getFullYear()
                          : method.yearTo;
                      let style =
                        year >= method.yearFrom && year <= yearTo
                          ? { backgroundColor: "black" }
                          : {};
                      return <TableCell key={year} style={style}></TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            )}
          {printableFertilityPreservation &&
            printableFertilityPreservation.length > 0 && (
              <TableBody className={classes.tableFertilityBody}>
                <TableRow>
                  <TableCell colSpan={yearFrame.length + 1}>
                    {t("module.provider.repli.chart.fertilityPreservation")}
                  </TableCell>
                </TableRow>
                {printableFertilityPreservation.map((method, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      {method.method === "Other"
                        ? method.otherMethod
                        : method.method}
                    </TableCell>
                    {yearFrame.map((year) => {
                      let yearTo =
                        method.yearTo === "Current method"
                          ? new Date().getFullYear()
                          : method.yearTo;
                      let style =
                        year >= method.yearFrom && year <= yearTo
                          ? { backgroundColor: "black" }
                          : {};
                      return <TableCell key={year} style={style}></TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            )}
          {printableFertilityTreatment &&
            printableFertilityTreatment.length > 0 && (
              <TableBody className={classes.tableFertilityBody}>
                <TableRow>
                  <TableCell colSpan={yearFrame.length + 1}>
                    {t("module.provider.repli.chart.fertilityTreatment")}
                  </TableCell>
                </TableRow>
                {printableFertilityTreatment.map((treatment, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{treatment.treatment}</TableCell>
                    {yearFrame.map((year) => {
                      let yearTo =
                        treatment.yearTo === "Current treatment"
                          ? new Date().getFullYear()
                          : treatment.yearTo;
                      let style =
                        year >= treatment.yearFrom && year <= yearTo
                          ? { backgroundColor: "black" }
                          : {};
                      return <TableCell key={year} style={style}></TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            )}
          {props.profile.cancers &&
            props.profile.cancers.length > 0 &&
            props.profile.cancers.map((cancer, i) => (
              <TableBody key={i} className={classes.tableCancerBody}>
                <TableRow>
                  <TableCell colSpan={yearFrame.length + 1}>
                    {t("common.substantive.cancerDiagnosis")} - {i + 1}{" "}
                  </TableCell>
                </TableRow>

                <TableRow hover>
                  <>
                    {cancer.siteOfCancer === "Other" ? (
                      <TableCell>{cancer.otherSiteOfCancer}</TableCell>
                    ) : (
                      <TableCell>{cancer.siteOfCancer}</TableCell>
                    )}
                  </>
                  {yearFrame.map((year) => {
                    let style =
                      cancer.year === year ? { backgroundColor: "black" } : {};
                    return <TableCell key={year} style={style}></TableCell>;
                  })}
                </TableRow>

                <TableRow className={classes.tableCancerTreatment}>
                  <TableCell colSpan={yearFrame.length + 1}>
                    {t("common.substantive.cancerTreatment")}- {i + 1}
                  </TableCell>
                </TableRow>

                {cancer.treatments.map((treatment) => (
                  <TableRow hover>
                    {treatment.treatmentType !== "None" && (
                      <>
                        <>
                          {treatment.treatmentType === "Other" ? (
                            <TableCell>
                              {treatment.otherTreatmentType}
                            </TableCell>
                          ) : (
                            <TableCell>{treatment.treatmentType}</TableCell>
                          )}
                        </>
                        {yearFrame.map((year) => {
                          let yearTo =
                            treatment.treatmentToYear === "Current treatment"
                              ? new Date().getFullYear()
                              : treatment.treatmentToYear;
                          let style =
                            year >= treatment.treatmentFromYear &&
                            year <= yearTo
                              ? { backgroundColor: "black" }
                              : {};
                          return (
                            <TableCell key={year} style={style}></TableCell>
                          );
                        })}
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            ))}
        </Table>
      </TableContainer>
    </>
  );
};

export default RepLIChart;
